/**
 * "Sign in with Microsoft", "Sign in with Google" (MB's R34) and "Sign in with LinkedIn" (decision 804): each
 * provider's official logo, used as supplied (`brand/providers/`), on a button in the provider's own colours for the
 * theme (tokens `provider-*`), as Microsoft's and Google's sign-in branding guidelines ask; LinkedIn's button is
 * neutral like the others, with LinkedIn's standard-colour [in] logo. The words name the button; the logo is
 * decorative.
 */
import type { ReactElement, ReactNode } from 'react';
import googleG from '../../../../brand/providers/google-g.svg';
import linkedinBug from '../../../../brand/providers/linkedin-in-bug.png';
import microsoftLogo from '../../../../brand/providers/microsoft-logo.svg';

export type SignInProvider = 'microsoft' | 'google' | 'linkedin';

const WORDS: Readonly<Record<SignInProvider, string>> = {
  microsoft: 'Sign in with Microsoft',
  google: 'Sign in with Google',
  linkedin: 'Sign in with LinkedIn',
};

/** Each logo at its own proportions (LinkedIn's file is 635 x 540 px, with its registered mark). */
const LOGO: Readonly<
  Record<SignInProvider, { readonly src: string; readonly width: number; readonly height: number }>
> = {
  microsoft: { src: microsoftLogo, width: 21, height: 21 },
  google: { src: googleG, width: 18, height: 18 },
  linkedin: { src: linkedinBug, width: 21, height: 18 },
};

export function ProviderButton({
  provider,
  onClick,
  disabled = false,
  note,
}: {
  readonly provider: SignInProvider;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  /** Words after the button's own, for example " · Available soon". */
  readonly note?: ReactNode;
}): ReactElement {
  const logo = LOGO[provider];
  return (
    <button
      type="button"
      className={`provider-btn provider-btn--${provider}`}
      disabled={disabled}
      onClick={onClick}
    >
      <img className="provider-btn__logo" src={logo.src} width={logo.width} height={logo.height} alt="" />
      <span>{WORDS[provider]}</span>
      {note}
    </button>
  );
}
